/**
 * Header - App header with branding and controls
 */

import { Sun, Moon, X, Download } from 'lucide-react'

function NormiesLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1024 1024" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="1024" height="1024" fill="white" rx="160" />
      <rect x="120" y="80" width="784" height="864" rx="28" fill="#2A2A2E" />
      <rect x="124" y="84" width="776" height="856" rx="26" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="4" />
      <rect x="140" y="98" width="44" height="56" rx="6" fill="#222226" />
      <rect x="310" y="108" width="404" height="200" rx="12" fill="#1C1C1F" />
      <rect x="318" y="114" width="260" height="188" rx="8" fill="#B0B0B4" />
      <rect x="322" y="118" width="252" height="5" rx="2" fill="#CCCCD0" />
      <line x1="318" y1="208" x2="578" y2="208" stroke="#929296" strokeWidth="2" />
      <rect x="346" y="140" width="200" height="130" rx="4" fill="#0E0E10" />
      <rect x="180" y="380" width="664" height="320" rx="8" fill="#F5F0E4" />
      <rect x="180" y="380" width="664" height="320" rx="8" fill="none" stroke="#E0D8C6" strokeWidth="2" />
      <line x1="220" y1="440" x2="804" y2="440" stroke="#DDD6C4" strokeWidth="1.5" />
      <line x1="220" y1="485" x2="804" y2="485" stroke="#DDD6C4" strokeWidth="1.5" />
      <line x1="220" y1="530" x2="804" y2="530" stroke="#DDD6C4" strokeWidth="1.5" />
      <line x1="220" y1="575" x2="804" y2="575" stroke="#DDD6C4" strokeWidth="1.5" />
      <line x1="220" y1="620" x2="804" y2="620" stroke="#DDD6C4" strokeWidth="1.5" />
      <line x1="220" y1="665" x2="804" y2="665" stroke="#DDD6C4" strokeWidth="1.5" />
      <rect x="392" y="770" width="240" height="140" rx="14" fill="#1C1C1F" />
      <circle cx="512" cy="840" r="32" fill="#2A2A2E" stroke="#1C1C1F" strokeWidth="4" />
      <circle cx="512" cy="840" r="14" fill="#1C1C1F" />
    </svg>
  )
}

interface HeaderProps {
  hasSession: boolean
  sessionTitle?: string
  isDark: boolean
  onToggleTheme: () => void
  onClear: () => void
}

export function Header({ hasSession, sessionTitle, isDark, onToggleTheme, onClear }: HeaderProps) {
  return (
    <header className="shrink-0 flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
      {/* Logo - links to main site */}
      <a
        href="https://landing.normies.work/"
        className="shrink-0 hover:opacity-80 transition-opacity"
        title="Normies"
        target="_blank"
        rel="noopener noreferrer"
      >
        <NormiesLogo className="w-9 h-9 rounded-lg" />
      </a>

      {/* Session title - fills middle space */}
      <div className="flex-1 min-w-0 flex justify-center">
        {sessionTitle && (
          <span className="text-sm font-semibold text-foreground truncate">
            {sessionTitle}
          </span>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
        {/* Download CTA */}
        <a
          href="https://landing.normies.work/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-foreground/[0.06] text-foreground/60 text-xs font-medium hover:bg-foreground/[0.1] hover:text-foreground/80 transition-colors"
        >
          <Download className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden sm:inline">Download Normies for Free</span>
          <span className="sm:hidden">Download</span>
        </a>

        {/* Clear button (when session is loaded) */}
        {hasSession && (
          <button
            onClick={onClear}
            className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
            title="Clear session"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={onToggleTheme}
          className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>
    </header>
  )
}
